import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import {
  listSignatures,
  createSignature,
  deleteSignature,
  FALLBACK_CAP,
} from '../lib/signatures';

/**
 * Loads and mutates the signed-in user's saved library for a single kind.
 *
 * `fieldKind` picks the library — 'signature' (default) or 'initials'. The two
 * libraries are independent: a hook instance for 'initials' never surfaces
 * entries from 'signature' or vice versa, so a Viewer can mount both side by
 * side without cross-contamination.
 *
 * The plan-tiered cap (Lite=1, Pro=3) is resolved server-side and returned on
 * every GET so the UI can render the right "+ New signature" affordance without
 * a second round-trip. The cap is combined across kinds — `used` reports only
 * this library's count, which is what the "N of M saved" copy wants to show,
 * but `capReached` reflects the combined total via the server's POST enforcement.
 * Guests get a resolved, empty state so callers can render the "not signed in"
 * branch without waiting on a network call.
 */
export function useSavedSignatures(fieldKind = 'signature') {
  const { user } = useAuth();
  const [signatures, setSignatures] = useState([]);
  const [plan, setPlan] = useState('lite');
  const [cap, setCap] = useState(FALLBACK_CAP);
  const [loading, setLoading] = useState(!!user);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setSignatures([]);
      setPlan('lite');
      setCap(FALLBACK_CAP);
      setLoading(false);
      setError(null);
      return () => { cancelled = true; };
    }
    setLoading(true);
    setError(null);
    listSignatures(fieldKind)
      .then((data) => {
        if (cancelled) return;
        setSignatures(data.signatures);
        setPlan(data.plan);
        setCap(data.cap);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, fieldKind]);

  const add = useCallback(async ({ kind, label, dataUrl, color, strokeWidth }) => {
    const created = await createSignature({ kind, fieldKind, label, dataUrl, color, strokeWidth });
    // Prepend — API returns rows created_at DESC, so the newest belongs on top.
    setSignatures((prev) => [created, ...prev]);
    return created;
  }, [fieldKind]);

  const remove = useCallback(async (id) => {
    // Optimistic — if the server rejects, restore the row.
    let prev;
    setSignatures((list) => {
      prev = list;
      return list.filter((s) => s.id !== id);
    });
    try {
      await deleteSignature(id);
    } catch (err) {
      if (prev) setSignatures(prev);
      throw err;
    }
  }, []);

  const used = signatures.length;
  return {
    signatures,
    loading,
    error,
    add,
    remove,
    plan,
    cap,
    used,
    capReached: used >= cap,
    signedIn: !!user,
    fieldKind,
  };
}
